/** Ordered school-specific player ids for career transfer dashboards. */

export type CareerStintRef = {
  playerId: string;
  team?: string;
  teamCode?: string | number;
};

export function collectCareerPlayerIdsFromRow(
  row: Record<string, any>,
  options?: {
    flattenTransferCareer?: (item: any) => Record<string, any>;
    getDashboardPlayerId?: (row: Record<string, any>) => string | undefined;
    loggedInTeamCode?: string | number | null;
  }
): string[] {
  const flatten =
    options?.flattenTransferCareer ??
    ((item: any) => {
      const flat: Record<string, any> = {};
      if (item?.metadata && typeof item.metadata === "object") {
        Object.entries(item.metadata).forEach(([key, value]) => {
          flat[key.toUpperCase()] = value;
        });
      }
      if (flat.PLAYER_ID === undefined && flat.ID !== undefined) {
        flat.PLAYER_ID = flat.ID;
      }
      return flat;
    });

  const getId =
    options?.getDashboardPlayerId ??
    ((r: Record<string, any>) => {
      const raw =
        r?.PLAYER_ID !== undefined &&
        r?.PLAYER_ID !== null &&
        String(r.PLAYER_ID).trim() !== ""
          ? r.PLAYER_ID
          : undefined;
      if (raw === undefined || raw === null || String(raw).trim() === "") {
        return undefined;
      }
      return String(raw);
    });

  const stints: CareerStintRef[] = [];
  const seen = new Set<string>();

  const pushStint = (playerId: string | undefined, team?: string, teamCode?: string | number) => {
    if (!playerId || seen.has(playerId)) return;
    seen.add(playerId);
    stints.push({ playerId, team, teamCode });
  };

  const parentId = getId(row);
  if (parentId) {
    pushStint(parentId, row.TEAM, row.TEAM_CODE);
  }

  if (row?.has_transfers === true && Array.isArray(row.transfer_careers)) {
    for (const career of row.transfer_careers) {
      const flat = flatten(career);
      pushStint(getId(flat), flat.TEAM, flat.TEAM_CODE);
    }
  }

  const loggedIn = options?.loggedInTeamCode;
  if (loggedIn !== undefined && loggedIn !== null && String(loggedIn).trim() !== "") {
    const matchIdx = stints.findIndex(
      (s) => s.teamCode !== undefined && String(s.teamCode) === String(loggedIn)
    );
    if (matchIdx > 0) {
      const [matched] = stints.splice(matchIdx, 1);
      stints.unshift(matched);
    }
  }

  return stints.map((s) => s.playerId);
}

export function buildPlayerDashboardPath(
  sportsCode: string | undefined,
  orderedPlayerIds: string[]
): string | null {
  if (!sportsCode || orderedPlayerIds.length === 0) return null;
  const [primary, ...rest] = orderedPlayerIds;
  const base = `/playerdashboard/${sportsCode}/${encodeURIComponent(primary)}`;
  if (rest.length === 0) return base;
  const q = rest.map((id) => encodeURIComponent(id)).join(",");
  return `${base}?careerIds=${q}`;
}

export function parseCareerPlayerIds(
  primaryPlayerId: string | undefined,
  careerIdsQuery: string | null
): string[] {
  const ordered: string[] = [];
  const seen = new Set<string>();
  const add = (raw: string | undefined | null) => {
    if (!raw) return;
    const id = String(raw).trim();
    if (!id || seen.has(id)) return;
    seen.add(id);
    ordered.push(id);
  };

  add(primaryPlayerId);
  if (careerIdsQuery) {
    careerIdsQuery.split(",").forEach((part) => add(decodeURIComponent(part.trim())));
  }
  return ordered;
}
